import eventBus from '@/services/eventBus';
import { useUserStore } from '@/stores/user';
import { IBetAppApi, IBetUpdateWS, IBetWS, IConfig, INotificationApi, IOrderAppApi, IOrderUpdateWS } from '@/types';
import { BetOrderStatus, BetStatus, EventName, EventPeriod, Theme, WL } from '@/constants';
import { BetItem } from '@/models/bet';
import { BetFactory } from '@/models/betFactory';
import { AppError } from '@/errors/appError';
import { OrderFactory } from '@/models/orderFactory';
import { OrderItem } from '@/models/order';
import { BettingData } from '@/models/bettingData';
import { NotificationItem } from '@/models/notification';
import equal from 'fast-deep-equal';
import { usePlatform } from '@/composables/usePlatform';
import { useBookmakerDictionary } from '@/stores/remoteDictionary/bookmaker';

export const useAppStore = defineStore('app', () => {
    const uniqAppId = ref<string | null>(null);
    let config = reactive<IConfig>({});
    const bets = ref<BetItem[]>([]);
    const orders = ref<OrderItem[]>([]);
    const notifications = ref<NotificationItem[]>([]);
    const lang = ref<string | null>(null);
    const ccy = ref<string | null>(null);
    const isInitialized = ref(false);
    const isBrowserTabInactive = ref(false);
    const lastBrowserTabInactive = ref<number | null>(null);
    const currentTheme = ref(Theme.Light);
    const feedSubscribeId = ref<number>(0);
    const betFeedData = reactive<Record<string, BettingData>>({});
    const loading = ref<boolean>(false);
    const timeDiff = ref<number>(0);

    const getBetFeedDataByCustomEvent = (customEventId: string): null | BettingData[] => {
        let result = [];
        for (const key in betFeedData) {
            const bettingData = betFeedData[key];

            if (bettingData.customEventId === customEventId) {
                result.push(bettingData);
            }
        }

        return result.length ? result : null;
    };

    const getBetsByParams = (params: Partial<Record<keyof BetItem, any>>): BetItem[] => {
        const paramEntries = Object.entries(params) as [keyof BetItem, any][];
        if (paramEntries.length === 0) return bets.value as BetItem[];

        return bets.value.filter((bet) =>
            paramEntries.every(([key, value]) => {
                const betValue = bet[key];
                if (Array.isArray(value)) {
                    return value.includes(betValue);
                }
                return betValue === value;
            })
        ) as BetItem[];
    };

    const getOrdersByParams = (params: Partial<Record<keyof OrderItem, any>>): OrderItem[] => {
        const paramEntries = Object.entries(params) as [keyof OrderItem, any][];
        if (paramEntries.length === 0) return orders.value as OrderItem[];

        return orders.value.filter((order) =>
            paramEntries.every(([key, value]) => {
                const orderValue = order[key];
                if (Array.isArray(value)) {
                    return value.includes(orderValue);
                }
                return orderValue === value;
            })
        ) as OrderItem[];
    };

    const userStore = useUserStore();

    const minStakes = computed(() => {
        const currency = userStore.currency.id;
        return config.minStakes![currency];
    });

    const botMinStakes = computed<number[][]>(() => {
        const platform = usePlatform();
        //todo it is a temporary solution because we don't have bookmakerDictionary for wl, need to rename Site to Platform or so
        if (platform.isBookmaker()) {
            return minStakes.value.bot;
        }
        const bookmakerDictionary = useBookmakerDictionary();

        const allowedBookmakers = bookmakerDictionary.items.filter((bookmaker) => bookmaker.inLiveFeed && bookmaker.isVisibleForPlayer && bookmaker.isActive);

        return minStakes.value.bot.filter((minStake) => allowedBookmakers.some((bookmaker) => bookmaker.id === minStake[0]));
    });

    const betOrderMinStakes = computed<number[][]>(() => {
        return minStakes.value.bet_order;
    });

    function setBets(rawBets: IBetAppApi[]) {
        let newFullCustomEventIdList: string[] = [];
        let partialCustomEventIdList: string[] = [];
        let newFullBetIdList: string[] = [];
        let partialBetIdList: string[] = [];

        let newFullBetList = [];
        let isFullUpdate = false;

        if (rawBets.length !== bets.value.length) isFullUpdate = true;

        for (const [index, raw] of rawBets.entries()) {
            const isValid = raw.period_type_id === EventPeriod.FullTime || (!raw.event_is_ht_already && !raw.event_is_2nd_half_started);

            if (!isValid) continue;

            const bet = BetFactory.createFromAppApi(raw);
            if (!isFullUpdate && bets.value[index]) {
                if (!equal(bet, bets.value[index])) {
                    if (bet.id === bets.value[index].id) {
                        bets.value[index] = bet;
                        partialCustomEventIdList.push(bet.customEventId);
                        partialBetIdList.push(bet.id);
                    } else isFullUpdate = true;
                }
            }
            newFullBetList.push(bet);
            newFullCustomEventIdList.push(bet.customEventId);
            newFullBetIdList.push(bet.id);

            if (bet.statusId === BetStatus.Rejected) {
                setTimeout(() => removeBet(bet), 60000);
            }
        }

        if (isFullUpdate) {
            console.log('Set full bet list');
            bets.value = newFullBetList;
            eventBus.emit(EventName.BetsChanged, { customEventIds: newFullCustomEventIdList, betIds: newFullBetIdList });
        } else if (partialBetIdList.length > 0) {
            eventBus.emit(EventName.BetsChanged, { customEventIds: partialCustomEventIdList, betIds: partialBetIdList });
        }
    }

    function addBet(data: IBetWS) {
        const platform = usePlatform();
        const bet = BetFactory.createFromWS(data);
        bets.value.unshift(bet);
        if (bet.statusId === BetStatus.Rejected) {
            setTimeout(() => removeBet(bet), 60000);
        }

        bet.showAddMessage();

        if (bet.orderId) {
            eventBus.emit(EventName.PageReload, { page: 'page-order' });
        }

        if (platform.isWL() && bet.statusId === BetStatus.Running) {
            bet.sendMessageToWL();
        }

        if (bet.statusId === BetStatus.Waiting && bet.orderId !== null) {
            //todo need to check it
            //eventBus.emit(EventName.OrderChanged, { id: bet.orderId });
        }
        eventBus.emit(EventName.BetsChanged, { customEventIds: [bet.customEventId], betIds: [bet.id] });
    }

    function updateBet(data: IBetUpdateWS) {
        const platform = usePlatform();
        const idx = bets.value.findIndex((bet) => bet.id === data.internal_id);
        if (idx === -1) {
            throw new AppError(`Bet ${data.internal_id} not found on updateBet`);
        }
        const bet = bets.value[idx];
        bet.statusId = data.status_id;
        bet.wl = data.wl as WL;
        bet.balance = data.balance;
        if (bet.statusId === BetStatus.Abnormal || bet.statusId === BetStatus.Void) {
            bets.value.splice(idx, 1);
        }
        if (bet.statusId === BetStatus.Rejected) {
            setTimeout(() => removeBet(bet as BetItem), 60000);
        }

        if (platform.isWL() && bet.statusId === BetStatus.Running) {
            bet.sendMessageToWL();
        }

        eventBus.emit(EventName.BetsChanged, { customEventIds: [bet.customEventId], betIds: [bet.id] });
    }

    function removeBet(bet: BetItem) {
        const idx = bets.value.findIndex((bet) => bet.id === bet.id);
        bets.value.splice(idx, 1);
        eventBus.emit(EventName.BetsChanged, { customEventIds: [bet.customEventId], betIds: [bet.id] });
    }

    function setNotifications(rawOrders: INotificationApi[]) {
        let newNotifications = [];
        for (const raw of rawOrders) {
            //data from ws without these fields
            raw.is_viewed = 0;
            raw.is_dismissed = 0;
            const order = new NotificationItem(raw);
            newNotifications.push(order);
        }

        notifications.value = newNotifications;
    }

    function setOrders(rawOrders: IOrderAppApi[]) {
        let newFullOrderList = [];
        let isFullUpdate = false;

        if (rawOrders.length !== orders.value.length) isFullUpdate = true;

        for (const [index, raw] of rawOrders.entries()) {
            const order = OrderFactory.createFromAppApi(raw);
            if (!isFullUpdate && orders.value[index]) {
                if (!equal(order, orders.value[index])) {
                    if (order.id === orders.value[index].id) orders.value[index] = order;
                    else isFullUpdate = true;
                }
            }

            newFullOrderList.push(order);
        }

        if (isFullUpdate) {
            orders.value = newFullOrderList;
        }
    }

    function addOrder(order: OrderItem) {
        const idx = orders.value.findIndex((orderItem) => orderItem.id === order.id);
        if (idx === -1) {
            orders.value.unshift(order);
            eventBus.emit(EventName.OrderAdded, order);
        } else {
            console.log(`Order #${order.id} already exists. Skipping addOrder`);
        }
    }

    function updateOrder(data: IOrderUpdateWS) {
        const idx = orders.value.findIndex((order) => order.id === data.internal_id);
        if (idx === -1) {
            throw new AppError(`Order ${data.internal_id} not found on updateOrder`);
        }
        const order = orders.value[idx] as OrderItem;

        order.closeReasonId = data.close_reason_id;
        order.statusId = data.status_id;
        if (data.stats) {
            order.betCount = data.stats.bet_count;
            order.betCountWaiting = data.stats.bet_waiting_count;
            order.activeBettingCount = data.stats.active_betting_count;
            order.sumBetStake = data.stats.sum_bet_stake;
            order.sumBetProfit = data.stats.sum_bet_profit;
            order.sumBetStakeWaiting = data.stats.sum_bet_waiting_stake;
            order.betOddsWaiting = data.stats.odds_waiting;
        }

        if (order.statusId === BetOrderStatus.Closed) {
            order.showCloseMessage();
            orders.value.splice(idx, 1);
        }
        eventBus.emit(EventName.OrderChanged, order);
    }

    setInterval(() => {
        orders.value.forEach((order) => order.calcLeftTime());
    }, 1000);

    function resetState() {
        userStore.login = null;
        userStore.type = null;
        userStore.locale = null;
        userStore.lang = null;
        userStore.token = null;

        userStore.balance.credit = 0;
        userStore.balance.balance = 0;
        userStore.balance.exposure = 0;
        userStore.balance.credit_limit = 0;
        userStore.balance.bet_balance = 0;
        userStore.balance.deposit = 0;

        uniqAppId.value = null;
        isInitialized.value = false;
    }

    function updateBettingPopup(data: any) {
        if (data.maxStake) {
            for (const uuid in data.maxStake) {
                for (const id in betFeedData) {
                    if (betFeedData[id].uuid === Number(uuid)) {
                        // betFeedData[id].oddsMaxStake = {
                        //     ...betFeedData[id].oddsMaxStake,
                        //     ...data.maxStake[uuid],
                        // };

                        // const incoming: Record<string, number> = data.maxStake[uuid] || {};
                        // const current: Record<string, number> = (betFeedData[id].oddsMaxStake as any) || {};
                        //
                        // // Remove bookmaker keys that are not present in incoming
                        // Object.keys(current).forEach((bookmaker) => {
                        //     if (!(bookmaker in incoming)) {
                        //         delete current[bookmaker];
                        //     }
                        // });
                        //
                        // // Add new keys and update changed values
                        // for (const bookmaker in incoming) {
                        //     const val = incoming[bookmaker];
                        //     if (current[bookmaker] !== val) {
                        //         current[bookmaker] = val;
                        //     }
                        // }
                        //
                        // betFeedData[id].oddsMaxStake = current as any;

                        // if (!isEqual(betFeedData[id].oddsMaxStake, data.maxStake[uuid])) {
                        if (!equal(betFeedData[id].oddsMaxStake, data.maxStake[uuid])) {
                            betFeedData[id].oddsMaxStake = {
                                ...betFeedData[id].oddsMaxStake,
                                ...data.maxStake[uuid],
                            };
                        }
                    }
                }
            }
        }

        if (data.extraOdds) {
            for (const uuid in data.extraOdds) {
                for (const id in betFeedData) {
                    if (betFeedData[id].uuid === Number(uuid)) {
                        betFeedData[id].setPreviousExtraPrices();
                        // betFeedData[id].extraOdds = data.extraOdds[uuid];

                        // const incoming: Record<string, Record<string, number>> = data.extraOdds[uuid] || {};
                        // const current: Record<string, Record<string, number>> = (betFeedData[id].extraOdds as any) || {};
                        //
                        // // Remove top-level keys (bookmakers) that are not present in incoming
                        // Object.keys(current).forEach((bookmaker) => {
                        //     if (!(bookmaker in incoming)) {
                        //         delete current[bookmaker];
                        //     }
                        // });
                        //
                        // // Merge and prune nested price maps per bookmaker
                        // for (const bookmaker in incoming) {
                        //     const incomingPrices = incoming[bookmaker] || {};
                        //     const currentPrices = current[bookmaker] || {};
                        //
                        //     // Remove prices not present anymore
                        //     Object.keys(currentPrices).forEach((priceKey) => {
                        //         if (!(priceKey in incomingPrices)) {
                        //             delete currentPrices[priceKey];
                        //         }
                        //     });
                        //
                        //     // Add/update changed prices
                        //     for (const priceKey in incomingPrices) {
                        //         const val = incomingPrices[priceKey];
                        //         if (currentPrices[priceKey] !== val) {
                        //             currentPrices[priceKey] = val;
                        //         }
                        //     }
                        //
                        //     current[bookmaker] = currentPrices;
                        // }
                        //
                        // betFeedData[id].extraOdds = current as any;

                        // if (!isEqual(betFeedData[id].extraOdds, data.extraOdds[uuid])) {
                        if (!equal(betFeedData[id].extraOdds, data.extraOdds[uuid])) {
                            betFeedData[id].extraOdds = data.extraOdds[uuid];
                        }
                    }
                }
            }
        }
    }

    const bettingCustomEventIds = computed(() => {
        const ids = Object.values(betFeedData).map((item) => item.customEventId);
        return Array.from(new Set(ids)).sort();
    });

    function setTimeDiff(serverTimestamp: number) {
        const diff = Date.now() - serverTimestamp * 1000;
        if (Math.abs(diff) > 1000) {
            //1 second
            timeDiff.value = diff;
        }
    }

    function syncedDate() {
        return Date.now() - timeDiff.value;
    }

    return {
        uniqAppId,
        config,
        bets,
        orders,
        lang,
        ccy,
        isInitialized,
        isBrowserTabInactive,
        lastBrowserTabInactive,
        currentTheme,
        getBetsByParams,
        getOrdersByParams,
        setBets,
        updateBettingPopup,
        addBet,
        updateBet,
        removeBet,
        addOrder,
        updateOrder,
        resetState,
        setOrders,
        feedSubscribeId,
        betFeedData,
        botMinStakes,
        betOrderMinStakes,
        setNotifications,
        notifications,
        bettingCustomEventIds,
        loading,
        getBetFeedDataByCustomEvent,
        timeDiff,
        setTimeDiff,
        syncedDate,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot));
}
